import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
} from "react";
import useMeterSplit from "../../hooks/use-meter-split";
import { CompteurType } from "../../models/compteur";
// @ts-expect-error pure js
import { ReactSelectStandalone } from "../UI/SelectStandalone";
// @ts-expect-error pure js
import TextInputStandalone from "../UI/TextInputStandalone";
import Button from "../UI/Button";
import Callout from "../UI/Callout";
import Text from "../UI/Text";
import { Table } from "../UI/Table";
import type { TenantChangeInfo } from "./TenantChangesAlert";
import TenantChangesAlert from "./TenantChangesAlert";
// @ts-expect-error pure js
import { currencyFormatter, dateConvert } from "../../api/Functions";
import styles from "./MeterSplitPanel.module.css";

interface ClosestReading {
  date_releve: string;
  valeur: number;
}

interface PropertyReading {
  property_id: number;
  property_name: string;
  debut_date?: string;
  fin_date?: string;
  debut_value?: number;
  fin_value?: number;
  missing?: ("debut" | "fin")[];
  closest_before?: ClosestReading;
  closest_after?: ClosestReading;
}

interface TenantPeriod {
  tenant_name: string;
  amount: number;
}

interface Split {
  property_id: number;
  property_name: string;
  amount: number;
  percentage: number;
  tenant_periods?: TenantPeriod[];
}

interface MeterSplitPanelProps {
  parentId: number;
  periodeDebut: string;
  periodeFin: string;
  montantTotal: number;
  onSplitCalculated?: (splits: Split[]) => void;
}

interface ExtendedSplitResult {
  status: "complete" | "incomplete";
  splits: Split[];
  tenant_changes?: TenantChangeInfo[];
  missing_readings?: PropertyReading[];
}

interface ReadingValues {
  debut?: number;
  fin?: number;
}

const COMPTEUR_TYPE_OPTIONS = [
  { value: "eau", label: CompteurType.EAU.description },
  { value: "eau_chaude", label: CompteurType.EAU_CHAUDE.description },
  { value: "elec", label: CompteurType.ELEC.description },
  { value: "gaz", label: CompteurType.GAZ.description },
  { value: "elec_hc", label: CompteurType.ELEC_HC.description },
  { value: "elec_hp", label: CompteurType.ELEC_HP.description },
];

export default function MeterSplitPanel({
  parentId,
  periodeDebut,
  periodeFin,
  montantTotal,
  onSplitCalculated,
}: MeterSplitPanelProps) {
  const { calculateSplit, createReadingsBatch, isCalculating, error } =
    useMeterSplit(parentId);

  const [compteurType, setCompteurType] = useState("eau");
  const [splitResult, setSplitResult] = useState<ExtendedSplitResult | null>(
    null,
  );
  const [manualReadings, setManualReadings] = useState<
    Record<number, ReadingValues>
  >({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setSplitResult(null);
    setManualReadings({});
  }, [periodeDebut, periodeFin, montantTotal, compteurType]);

  const doCalculate = useCallback(
    async (readings: Record<number, ReadingValues> = {}) => {
      if (!periodeDebut || !periodeFin || !montantTotal) return;

      try {
        const result = (await calculateSplit({
          compteur_type: compteurType,
          periode_debut: periodeDebut,
          periode_fin: periodeFin,
          montant_total: montantTotal,
          manual_readings: readings as Record<number, number>,
        })) as ExtendedSplitResult;

        setSplitResult(result);

        if (result.status === "complete" && onSplitCalculated) {
          onSplitCalculated(result.splits);
        }
      } catch (err) {
        console.error("Error calculating meter split:", err);
      }
    },
    [
      calculateSplit,
      compteurType,
      periodeDebut,
      periodeFin,
      montantTotal,
      onSplitCalculated,
    ],
  );

  const handleCalculateClick = useCallback(() => {
    doCalculate(manualReadings);
  }, [doCalculate, manualReadings]);

  const updateManualReading = useCallback(
    (propertyId: number, field: "debut" | "fin", value: string) => {
      setManualReadings((prev) => {
        const propertyReadings = { ...(prev[propertyId] || {}) };
        if (value === "") {
          delete propertyReadings[field];
        } else {
          propertyReadings[field] = parseFloat(value);
        }
        return { ...prev, [propertyId]: propertyReadings };
      });
    },
    [],
  );

  const handleSaveReadings = useCallback(async () => {
    if (!compteurType) return;

    const readingsToSave: Array<{
      id_bien: number;
      type: string;
      date_releve: string;
      valeur: number;
    }> = [];

    Object.entries(manualReadings).forEach(([propertyId, vals]) => {
      const id = parseInt(propertyId);
      const missingReading = splitResult?.missing_readings?.find(
        (r) => r.property_id === id,
      );
      const debutDate = missingReading?.debut_date || periodeDebut;
      const finDate = missingReading?.fin_date || periodeFin;

      if (vals.debut != null && debutDate) {
        readingsToSave.push({
          id_bien: id,
          type: compteurType,
          date_releve: debutDate,
          valeur: vals.debut,
        });
      }
      if (vals.fin != null && finDate) {
        readingsToSave.push({
          id_bien: id,
          type: compteurType,
          date_releve: finDate,
          valeur: vals.fin,
        });
      }
    });

    if (readingsToSave.length === 0) return;

    setIsSaving(true);
    try {
      await createReadingsBatch(readingsToSave);
      await doCalculate(manualReadings);
    } catch (err) {
      console.error("Error saving readings:", err);
    } finally {
      setIsSaving(false);
    }
  }, [
    compteurType,
    createReadingsBatch,
    doCalculate,
    manualReadings,
    periodeDebut,
    periodeFin,
    splitResult,
  ]);

  const canCalculate = Boolean(periodeDebut && periodeFin && montantTotal);

  const rows = useMemo(() => {
    const missing = splitResult?.missing_readings ?? [];
    const splits = splitResult?.splits ?? [];

    if (missing.length > 0) {
      return missing.map((reading) => {
        const correspondingSplit = splits.find(
          (s) => s.property_id === reading.property_id,
        );
        return {
          propertyId: reading.property_id,
          propertyName: reading.property_name,
          debutDate: reading.debut_date || periodeDebut,
          finDate: reading.fin_date || periodeFin,
          debutValue:
            manualReadings[reading.property_id]?.debut?.toString() ??
            reading.debut_value?.toString() ??
            "",
          finValue:
            manualReadings[reading.property_id]?.fin?.toString() ??
            reading.fin_value?.toString() ??
            "",
          isDebutMissing: reading.missing?.includes("debut") ?? false,
          isFinMissing: reading.missing?.includes("fin") ?? false,
          closestBefore: reading.closest_before,
          closestAfter: reading.closest_after,
          amount: correspondingSplit?.amount,
          percentage: correspondingSplit?.percentage,
          tenantPeriods: correspondingSplit?.tenant_periods,
        };
      });
    }

    return splits.map((split) => ({
      propertyId: split.property_id,
      propertyName: split.property_name,
      debutDate: periodeDebut,
      finDate: periodeFin,
      debutValue: manualReadings[split.property_id]?.debut?.toString() ?? "",
      finValue: manualReadings[split.property_id]?.fin?.toString() ?? "",
      isDebutMissing: false,
      isFinMissing: false,
      closestBefore: undefined,
      closestAfter: undefined,
      amount: split.amount,
      percentage: split.percentage,
      tenantPeriods: split.tenant_periods,
    }));
  }, [splitResult, manualReadings, periodeDebut, periodeFin]);

  const hasUnsavedManualReadings = Object.keys(manualReadings).length > 0;

  return (
    <div className={styles.wrapper}>
      <ReactSelectStandalone
        label="Type de compteur"
        options={COMPTEUR_TYPE_OPTIONS}
        value={COMPTEUR_TYPE_OPTIONS.find((o) => o.value === compteurType)}
        onChange={(option: { value: string } | null) => {
          setCompteurType(option?.value || "eau");
        }}
        isClearable={false}
      />

      <div>
        <Text weight="bold">Répartition des compteurs</Text>
        <Text color="muted">
          Veuillez entrer les index de compteur manquants pour calculer la
          répartition.
        </Text>
      </div>

      <div>
        <Button
          type="button"
          onClick={handleCalculateClick}
          disabled={!canCalculate || isCalculating}
          isLoading={isCalculating}
        >
          Calculer
        </Button>
      </div>

      {!canCalculate && !splitResult && (
        <Callout variant="info">
          Veuillez renseigner la période de facturation et le montant total pour
          calculer la répartition par compteur.
        </Callout>
      )}

      <Callout variant="danger">{error}</Callout>

      {splitResult?.tenant_changes && splitResult.tenant_changes.length > 0 && (
        <TenantChangesAlert tenantChanges={splitResult.tenant_changes} />
      )}

      {rows.length > 0 && (
        <Table variant="card">
          <Table.Header>
            <Table.Column>Appartement</Table.Column>
            <Table.Column align="center" className={styles.inputColumn}>
              Index
              <br />
              {dateConvert(periodeDebut)}
            </Table.Column>
            <Table.Column align="center" className={styles.inputColumn}>
              Index
              <br />
              {dateConvert(periodeFin)}
            </Table.Column>
            <Table.Column align="right">
              Montant
              <br />
              <Text as="span" variant="deci" color="muted">
                % du total
              </Text>
            </Table.Column>
          </Table.Header>
          <Table.Body>
            {rows.map((row) => (
              <Fragment key={row.propertyId}>
                <Table.Row>
                  <Table.Cell>
                    <Text as="span">{row.propertyName}</Text>
                  </Table.Cell>
                  <Table.Cell align="center">
                    <TextInputStandalone
                      type="number"
                      step="0.01"
                      placeholder={
                        row.closestBefore?.valeur?.toString() ?? "0.00"
                      }
                      value={row.debutValue}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        updateManualReading(
                          row.propertyId,
                          "debut",
                          e.target.value,
                        )
                      }
                      mandatory={row.isDebutMissing}
                      marginForm=""
                      className={styles.inputColumn}
                    />
                  </Table.Cell>
                  <Table.Cell align="center">
                    <TextInputStandalone
                      type="number"
                      step="0.01"
                      placeholder={
                        row.closestAfter?.valeur?.toString() ?? "0.00"
                      }
                      value={row.finValue}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        updateManualReading(
                          row.propertyId,
                          "fin",
                          e.target.value,
                        )
                      }
                      mandatory={row.isFinMissing}
                      marginForm=""
                      className={styles.inputColumn}
                    />
                  </Table.Cell>
                  <Table.Cell align="right">
                    <div>
                      <Text as="div" weight="bold">
                        {row.amount != null
                          ? currencyFormatter(row.amount)
                          : "—"}
                      </Text>
                      {row.percentage != null && (
                        <Text as="div" variant="deci" color="muted">
                          {row.percentage.toFixed(1)}%
                        </Text>
                      )}
                    </div>
                  </Table.Cell>
                </Table.Row>

                {Array.isArray(row.tenantPeriods) &&
                  row.tenantPeriods.length > 1 &&
                  row.tenantPeriods.map((period, idx) => (
                    <Table.Row key={`${row.propertyId}-tenant-${idx}`}>
                      <Table.Cell colSpan={3}>
                        <Text as="span" variant="deci" color="muted">
                          {period.tenant_name}
                        </Text>
                      </Table.Cell>
                      <Table.Cell align="right">
                        <Text as="span" variant="deci" weight="bold">
                          {currencyFormatter(period.amount)}
                        </Text>
                      </Table.Cell>
                    </Table.Row>
                  ))}
              </Fragment>
            ))}
          </Table.Body>
        </Table>
      )}

      {hasUnsavedManualReadings && (
        <div>
          <Button
            type="button"
            variant="secondary"
            onClick={handleSaveReadings}
            disabled={isCalculating || isSaving}
            isLoading={isSaving}
          >
            Enregistrer les relevés
          </Button>
        </div>
      )}
    </div>
  );
}
